import React, { useEffect, useState } from "react";
import useClockState from "../useClockState";
import useOnlineOrders, { staffRole } from "../useOnlineOrders";
import OnlineOrdersPanel from "./OnlineOrdersPanel";
import ClockWindow from "./ClockWindow";
import { colors, fonts, space, radius } from "../theme";

// Wraps the Kitchen / Waiter screen with (a) an always-visible "Clock in / out" button when the Employee module
// is on and (b) an "Online orders" tab when the restaurant takes online orders. The badge and the new-order
// sound keep working while the other tab is showing.
export default function TabbedStation({ Board, title, dark }) {
  const { onlineEnabled, blocked, clockButton, refresh } = useClockState();
  const role = staffRole();
  const { data, error, reload } = useOnlineOrders(onlineEnabled);
  const [tab, setTab] = useState("main");
  const [clockOpen, setClockOpen] = useState(false);
  useEffect(() => {
    const open = () => setClockOpen(true);
    window.addEventListener("open-clock", open);
    return () => window.removeEventListener("open-clock", open);
  }, []);
  if (!onlineEnabled && !clockButton) return <Board />;
  const front = ["owner", "manager", "waiter"].includes(role);
  const badge = data ? (front ? data.counts.pending : data.counts.kitchen) : 0;
  const bar = { display: "flex", gap: 6, alignItems: "center", padding: `${space[2]} ${space[3]}`, background: dark ? "#0b1020" : colors.surface, borderBottom: `1px solid ${colors.border}`, fontFamily: fonts.body, position: "sticky", top: 0, zIndex: 20, flexWrap: "wrap" };
  const btn = (on) => ({ padding: "8px 16px", borderRadius: radius.md, border: "none", cursor: "pointer", fontSize: 14, fontWeight: on ? 700 : 500, background: on ? colors.brandBlue : "transparent", color: on ? "#fff" : (dark ? "#cbd5e1" : colors.textPrimary) });
  const clockLabel = clockButton ? (clockButton.linked ? (clockButton.clocked_in ? `Clock out · since ${clockButton.since}` : (clockButton.has_earlier_shift_today ? "+ Clock in (new shift today)" : "Clock in")) : "Clock in / out") : "";
  return (
    <div style={{ background: dark ? "#0b1020" : colors.bgPage, minHeight: "100vh" }}>
      <div style={bar} role="tablist" data-testid="station-tabs">
        {onlineEnabled && (
          <>
            <button role="tab" aria-selected={tab === "main"} style={btn(tab === "main")} onClick={() => setTab("main")}>{title}</button>
            <button role="tab" aria-selected={tab === "online"} style={btn(tab === "online")} onClick={() => setTab("online")} data-testid="online-tab">
              Online orders {badge > 0 && <span style={{ background: "#dc2626", color: "#fff", borderRadius: 999, padding: "1px 8px", fontSize: 12, marginLeft: 4 }} data-testid="online-badge">{badge}</span>}
            </button>
          </>
        )}
        {clockButton && (
          <button onClick={() => setClockOpen(true)} data-testid="clock-button" style={{ marginLeft: "auto", padding: "8px 14px", borderRadius: radius.md, border: `1px solid ${clockButton.clocked_in ? "#16a34a" : (dark ? "#475569" : colors.border)}`, background: clockButton.clocked_in ? "#dcfce7" : "transparent", color: clockButton.clocked_in ? "#166534" : (dark ? "#cbd5e1" : colors.textPrimary), fontSize: 14, cursor: "pointer" }}>
            {clockLabel}
          </button>
        )}
      </div>
      {tab === "main" || !onlineEnabled ? <Board /> : <OnlineOrdersPanel role={role} readOnly={blocked} data={data} error={error} reload={reload} />}
      {clockOpen && clockButton && <ClockWindow info={clockButton} onClose={() => setClockOpen(false)} onDone={() => { setClockOpen(false); refresh(); window.dispatchEvent(new Event("focus")); }} />}
    </div>
  );
}
